import { useQueryClient } from "@tanstack/react-query";
import { CircleAlert } from "lucide-react";
import { useId, useState, type FormEvent, type ReactNode } from "react";
import { schluessel, useStatus } from "../api/abfragen.ts";
import { api, ApiFehler } from "../api/klient.ts";
import { Marke } from "../ui/Marke.tsx";
import "./zugang.css";

function Rahmen({ children }: { children: ReactNode }) {
  return (
    <div className="zugang">
      <div className="zugang-kopf">
        <Marke />
        GUI-Creator
      </div>
      <main className="zugang-mitte">{children}</main>
      <div className="zugang-fuss">Menüs für Plugins planen, gestalten und freigeben.</div>
    </div>
  );
}

function Fehler({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <div className="meldung meldung-fehler" role="alert">
      <CircleAlert size={14} aria-hidden="true" />
      <span>{text}</span>
    </div>
  );
}

function useAbsenden(pfad: string, pruefen?: () => string | null) {
  const qc = useQueryClient();
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const absenden = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const daten = new FormData(e.currentTarget);
    const vorab = pruefen?.() ?? null;
    if (vorab) return setFehler(vorab);
    setLaeuft(true);
    setFehler(null);
    try {
      const code = daten.get("code");
      await api(pfad, { methode: "POST", body: { name: String(daten.get("name") ?? ""), passwort: String(daten.get("passwort") ?? ""), ...(code !== null ? { code: String(code) } : {}) } });
      await qc.invalidateQueries();
      await qc.refetchQueries({ queryKey: schluessel.status });
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : "Unbekannter Fehler.");
    } finally {
      setLaeuft(false);
    }
  };
  return { fehler, laeuft, absenden };
}

export function Einrichtung() {
  const id = useId();
  const mitCode = !!useStatus().data?.codeNoetig;
  const [passwort, setPasswort] = useState("");
  const [wiederholung, setWiederholung] = useState("");
  const { fehler, laeuft, absenden } = useAbsenden("/api/einrichtung", () =>
    passwort !== wiederholung ? "Die Passwörter stimmen nicht überein." : null,
  );
  return (
    <Rahmen>
      <form className="zugang-form" onSubmit={absenden}>
        <header>
          <h1>GUI-Creator einrichten</h1>
          <p className="leise">Das erste Konto wird Eigentümer und verwaltet alle weiteren Konten.</p>
        </header>
        <div className="feld">
          <label htmlFor={`${id}-name`}>Name</label>
          <input id={`${id}-name`} name="name" className="eingabe" autoComplete="username" required minLength={2} maxLength={32} autoFocus />
        </div>
        <div className="feld">
          <label htmlFor={`${id}-pw`}>Passwort</label>
          <input id={`${id}-pw`} name="passwort" type="password" className="eingabe" autoComplete="new-password" required minLength={10} value={passwort} onChange={(e) => setPasswort(e.target.value)} />
          <span className="feld-hinweis">Mindestens 10 Zeichen.</span>
        </div>
        <div className="feld">
          <label htmlFor={`${id}-pw2`}>Passwort wiederholen</label>
          <input id={`${id}-pw2`} type="password" className="eingabe" autoComplete="new-password" required value={wiederholung} onChange={(e) => setWiederholung(e.target.value)} />
        </div>
        {mitCode && (
          <div className="feld">
            <label htmlFor={`${id}-code`}>Einrichtungscode</label>
            <input id={`${id}-code`} name="code" className="eingabe mono" autoComplete="off" required />
            <span className="feld-hinweis">Steht als EINRICHTUNG_CODE in der .env auf dem Server.</span>
          </div>
        )}
        <Fehler text={fehler} />
        <button className="knopf knopf-primaer knopf-breit" disabled={laeuft}>
          {laeuft ? "Wird eingerichtet …" : "Einrichten"}
        </button>
      </form>
    </Rahmen>
  );
}

export function Anmeldung() {
  const id = useId();
  const { fehler, laeuft, absenden } = useAbsenden("/api/anmelden");
  return (
    <Rahmen>
      <form className="zugang-form" onSubmit={absenden}>
        <header>
          <h1>Anmelden</h1>
        </header>
        <div className="feld">
          <label htmlFor={`${id}-name`}>Name</label>
          <input id={`${id}-name`} name="name" className="eingabe" autoComplete="username" required autoFocus />
        </div>
        <div className="feld">
          <label htmlFor={`${id}-pw`}>Passwort</label>
          <input id={`${id}-pw`} name="passwort" type="password" className="eingabe" autoComplete="current-password" required />
        </div>
        <Fehler text={fehler} />
        <button className="knopf knopf-primaer knopf-breit" disabled={laeuft}>
          {laeuft ? "Wird angemeldet …" : "Anmelden"}
        </button>
      </form>
    </Rahmen>
  );
}
